<template>
  <el-dialog
    :title="mode === 'view' ? '查看授权书' : mode === 'check' ? '审核授权书' : '上传授权书'"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    @close="resetForm">
    
    <div class="authorization-container">
      <!-- 模板下载 - 仅在add模式下显示 -->
      <div v-if="mode === 'add'" class="download-section">
        <el-button 
          type="primary" 
          icon="el-icon-download"
          @click="downloadTemplate">
          下载授权书模板
        </el-button>
        <p class="tip">请下载模板，填写完整信息并加盖公章后上传</p>
      </div>

      <!-- 文件上传 - 仅在add模式下显示 -->
      <div v-if="mode === 'add'" class="upload-section">
        <el-upload
          ref="upload"
          :action="uploadUrl"
          :headers="uploadHeaders"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :file-list="fileList"
          :auto-upload="true"
          name="fileVal"
          accept=".pdf,.jpg,.png,.jpeg,.docx,.doc"
          list-type="picture-card">
          <i class="el-icon-plus"></i>
          <div slot="tip" class="el-upload__tip">
            支持格式：PDF、JPG、PNG、JPEG、DOCX、DOC，文件大小不超过10MB
          </div>
        </el-upload>
      </div>

      <!-- 已上传文件预览 -->
      <div v-if="authorizationUrl" class="preview-section">
        <h4>当前授权书：</h4>
        <div class="file-preview">
          <div v-if="isImageFile(authorizationUrl)" class="image-preview">
            <img :src="authorizationUrl" alt="授权书预览" @click="previewFile(authorizationUrl)">
          </div>
          <div v-else class="file-link">
            <el-link :href="authorizationUrl" target="_blank" type="primary">
              <i class="el-icon-document"></i>
              {{ getFileName(authorizationUrl) }}
            </el-link>
            <span class="file-tip">（点击查看文件）</span>
          </div>
          <el-button 
            v-if="mode === 'add'"
            type="danger" 
            size="mini" 
            icon="el-icon-delete"
            @click="removeFile"
            style="margin-left: 10px;">
            删除
          </el-button>
        </div>
      </div>

      <!-- view模式下无授权书的提示 -->
      <div v-if="mode === 'view' && !authorizationUrl" class="no-file-tip">
        <el-alert
          title="暂无授权书"
          type="info"
          description="该订单尚未上传授权书"
          :closable="false">
        </el-alert>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      
      <!-- 上传模式下的保存按钮 -->
      <el-button 
        v-if="mode === 'add'"
        type="primary" 
        @click="save"
        :loading="saving">
        保存
      </el-button>
      
      <!-- 审核模式下的审核按钮 -->
      <template v-if="mode === 'check'">
        <el-button 
          type="danger" 
          @click="handleReject"
          :loading="saving">
          驳回
        </el-button>
        <el-button 
          type="success" 
          @click="handleApprove"
          :loading="saving">
          通过
        </el-button>
      </template>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="80%"
      append-to-body>
      <img :src="previewImageUrl" style="width: 100%; height: auto;" alt="预览图片">
    </el-dialog>
  </el-dialog>
</template>

<script>
import { ygbOrderUploadAuthorization, ygbOrderAuthorizationCheck } from '@/api/member'

export default {
  name: 'UploadAuthorization',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'add' // add: 上传模式, view: 查看模式, check: 审核模式
    }
  },
  data() {
    return {
      dialogVisible: false,
      authorizationUrl: '',
      fileList: [],
      saving: false,
      previewVisible: false,
      previewImageUrl: '',
      uploadUrl: process.env.VUE_APP_FILE_UPLOAD_URL || process.env.VUE_APP_BASE_URL + '/res/file/upload',
      uploadHeaders: {
        'Authorization': 'Bearer ' + this.$store.getters.token
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadOrderInfo()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 加载订单信息
    loadOrderInfo() {
      if (this.rowData && this.rowData.authorization) {
        this.authorizationUrl = this.rowData.authorization
      }
    },

    // 下载模板
    downloadTemplate() {
      const templateUrl = process.env.VUE_APP_BASE_URL + '/static/雇主责任险人员网络变更授权书.docx'
      const link = document.createElement('a')
      link.href = templateUrl
      link.target = '_blank'
      link.download = '雇主责任险人员网络变更授权书.docx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 上传前检查
    beforeUpload(file) {
      const isValidType = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
                          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
                          .includes(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isValidType) {
        this.$message.error('只能上传 PDF、JPG、PNG、JPEG、DOCX、DOC 格式的文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },

    // 上传成功
    handleUploadSuccess(response, file, fileList) {
      if (response && response.data) {
        this.authorizationUrl = response.data
        this.$message.success('文件上传成功')
        this.fileList = []
      } else {
        this.$message.error('文件上传失败')
      }
    },

    // 上传失败
    handleUploadError(err, file, fileList) {
      console.error('上传失败:', err)
      this.$message.error('文件上传失败，请重试')
      this.fileList = []
    },

    // 判断是否为图片文件
    isImageFile(url) {
      if (!url) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp']
      return imageExtensions.some(ext => url.toLowerCase().includes(ext))
    },

    // 获取文件名
    getFileName(url) {
      if (!url) return ''
      const parts = url.split('/')
      return parts[parts.length - 1] || '授权书文件'
    },

    // 预览文件
    previewFile(url) {
      if (this.isImageFile(url)) {
        this.previewImageUrl = url
        this.previewVisible = true
      } else {
        window.open(url, '_blank')
      }
    },

    // 删除文件
    removeFile() {
      this.$confirm('确定要删除当前授权书吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.authorizationUrl = ''
        this.$message.success('删除成功')
      })
    },

    // 保存
    save() {
      if (!this.authorizationUrl) {
        this.$message.warning('请先上传授权书文件')
        return
      }

      this.saving = true
      const data = {
        id: this.rowData.id,
        authorization: this.authorizationUrl
      }

      ygbOrderUploadAuthorization(data).then(response => {
        if (response && response.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(response.err_msg || '保存失败')
        }
      }).catch(error => {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      }).finally(() => {
        this.saving = false
      })
    },

    // 审核通过
    handleApprove() {
      this.$confirm('确定要通过此授权书吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitAudit(1, '')
      })
    },

    // 审核驳回
    handleReject() {
      this.$prompt('请输入驳回原因', '驳回授权书', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return '请输入驳回原因'
          }
          return true
        }
      }).then(({ value }) => {
        this.submitAudit(2, value.trim())
      })
    },

    // 提交审核
    submitAudit(status, reason) {
      this.saving = true
      const data = {
        id: this.rowData.id,
        status: status, // 1: 通过, 2: 驳回
        reason: reason
      }

      ygbOrderAuthorizationCheck(data).then(response => {
        if (response && response.err_code === 0) {
          this.$message.success(status === 1 ? '审核通过' : '已驳回')
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(response.err_msg || '审核失败')
        }
      }).catch(error => {
        console.error('审核失败:', error)
        this.$message.error('审核失败')
      }).finally(() => {
        this.saving = false
      })
    },

    // 重置表单
    resetForm() {
      this.authorizationUrl = ''
      this.fileList = []
      this.saving = false
      this.previewVisible = false
      this.previewImageUrl = ''
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.authorization-container {
  padding: 20px 0;
}

.download-section {
  margin-bottom: 30px;
  text-align: center;
}

.download-section .tip {
  margin-top: 10px;
  color: #666;
  font-size: 14px;
}

.upload-section {
  margin-bottom: 30px;
}

.preview-section {
  margin-top: 20px;
}

.preview-section h4 {
  margin-bottom: 15px;
  color: #333;
}

.file-preview {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.image-preview img {
  max-width: 100px;
  max-height: 100px;
  cursor: pointer;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.file-link {
  display: flex;
  align-items: center;
}

.file-tip {
  margin-left: 10px;
  color: #999;
  font-size: 12px;
}

.dialog-footer {
  text-align: right;
}

.no-file-tip {
  margin-top: 20px;
}

/* 上传组件样式调整 */
::v-deep .el-upload--picture-card {
  width: 100px;
  height: 100px;
  line-height: 100px;
}

::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: 100px;
  height: 100px;
}
</style>